<template>
  <tg-layout prefixCls="tg-table">
    <template slot="header">
      <el-card shadow="naver" v-show="$slots['header']">
        <slot name="header"></slot>
      </el-card>
    </template>
    <el-table
      ref="ElTable"
      :border="false"
      :data="tableData"
      v-bind="{ ...$attrs }"
      v-on="$listeners"
      v-loading="loading"
      :element-loading-text="loadingLabel"
      element-loading-spinner="el-icon-loading"
      element-loading-background="rgba(0, 0, 0, 0.8)"
      :max-height="maxHeight"
      :class="tableClass"
      @expand-change="handleOnExpanded"
    >
      <template slot="empty">
        <div v-if="!fetched">
          <i class="el-icon-loading"></i>
          <span>{{ loadingLabel }}</span>
        </div>
        <tg-nodata v-else></tg-nodata>
      </template>
      <template v-for="(column, index) in computedColumns">
        <el-table-column
          :key="index + 's'"
          v-if="column.type === 'selection'"
          :selectable="selectable"
          type="selection"
          :fixed="column.fixed"
          :reserve-selection="reserveSelection"
        ></el-table-column>
        <el-table-column
          :key="index + 'i'"
          v-else-if="column.type === 'index'"
          :fixed="column.fixed"
          type="index"
          :index="column.index"
        ></el-table-column>
        <el-table-column
          :key="index + 'e'"
          v-else-if="column.type === 'expand'"
          :fixed="column.fixed"
          type="expand"
          class-name="tg-table--expand"
        >
          <template slot-scope="props">
            <slot name="expand" :column="column" :row="props.row"></slot>
          </template>
        </el-table-column>
        <el-table-column
          :key="index + 'empty'"
          v-else-if="column.type === 'empty'"
          :fixed="column.fixed"
          :width="column.width"
          label=""
          :class-name="column.class || ''"
        >
        </el-table-column>
        <template v-else>
          <el-table-column
            v-if="column.children"
            align="center"
            :key="column.name + index + 'd'"
            :label="column.title"
            :render-header="column.renderHeader"
          >
            <el-table-column
              v-for="(sub, i) in column.children"
              :key="sub.name + i + index"
              :label="sub.title"
              :prop="sub.name"
              :sortable="sub.sortable"
              :sort-by="sub['sort-by']"
              :sort-method="
                !sortMethods[sub.name] ? null : sortMethods[sub.name]
              "
              :sort-orders="['ascending', 'descending']"
              :fixed="sub.fixed"
              :formatter="formatterColumn"
              :column-key="sub.name"
              :filters="sub.filters"
              :filter-placement="sub['filter-placement']"
              :filter-multiple="sub['filter-multiple']"
              :filter-method="
                !filterMethods[sub.name] ? null : filterMethods[sub.name]
              "
              :filtered-value="sub['filtered-value']"
              :align="sub.align || 'center'"
              :header-align="headerAlign"
              :width="sub.width"
              :render-header="sub.renderHeader"
              :class-name="sub.class || ''"
              :label-class-name="sub.headerClass"
            >
              <template v-if="sub.headerSlot">
                <slot
                  slot="header"
                  :name="'header.' + sub.headerSlot"
                  :column="sub"
                ></slot>
              </template>
              <template v-if="sub.slot" v-slot="props">
                <tg-table-slot
                  :column="{ ...sub, parent: column }"
                  :row="props.row"
                ></tg-table-slot>
              </template>
              <template v-else-if="'default' in sub || 'format' in sub" v-slot="props">
                <!-- <template v-if="!props.row[props.column.columnKey]">
                  <span>{{ sub.default }}</span>
                </template> -->
                <template>{{
                  formatterColumn(
                    props.row,
                    props.column,
                    props.row[props.column.columnKey]
                  )
                }}</template>
              </template>
            </el-table-column>
          </el-table-column>
          <el-table-column
            v-else
            :key="column.name + index + 'c'"
            :label="column.title"
            :prop="column.name"
            :sortable="column.sortable"
            :sort-by="column['sort-by']"
            :sort-method="
              !sortMethods[column.name] ? null : sortMethods[column.name]
            "
            :sort-orders="['ascending', 'descending']"
            :fixed="column.fixed"
            :formatter="formatterColumn"
            :column-key="column.name"
            :filters="column.filters"
            :filter-placement="column['filter-placement']"
            :filter-multiple="column['filter-multiple']"
            :filter-method="
              !filterMethods[column.name] ? null : filterMethods[column.name]
            "
            :filtered-value="column['filtered-value']"
            :align="column.align || 'center'"
            :header-align="headerAlign"
            :width="column.width"
            :render-header="column.renderHeader"
            :class-name="column.class || ''"
            :label-class-name="column.headerClass"
          >
            <template v-if="column.headerSlot">
              <slot
                slot="header"
                :name="'header.' + column.headerSlot"
                :column="column"
              ></slot>
            </template>
            <template v-if="column.slot" v-slot="props">
              <tg-table-slot :column="column" :row="props.row"></tg-table-slot>
            </template>
            <template v-else-if="'default' in column || 'format' in column" v-slot="props">
              <template>{{
                formatterColumn(
                  props.row,
                  props.column,
                  props.row[props.column.columnKey]
                )
              }}</template>
            </template>
          </el-table-column>
        </template>
      </template>
    </el-table>
    <tg-pagination
      v-if="isShowPagination"
      :page="currentPage"
      :perPages="perPages"
      :totalPage="currentTotalPage"
      :value="perPage"
      :showPerPage="showPerPage"
      @pageChanged="handlePageChange"
      @perPageChanged="handlePerPageChange"
    ></tg-pagination>
  </tg-layout>
</template>

<script>
import TgLayout from "@/layout/TgLayout";
import TgPagination from "@/components/pagination/TgPagination";
import { ElTableProps } from "./config";
import TgNodata from "@/components/TgNodata";
import formatter from "@/utils/formatter";
import _ from "lodash";
import TgTableSlot from "./render";

export default {
  name: "TgTable",
  components: {
    TgNodata,
    TgTableSlot,
    TgPagination,
    TgLayout,
  },
  provide() {
    return {
      tableRoot: this,
    };
  },
  inject: ["$layout"],
  data() {
    return {
      computedColumns: [],
      fetched: false,
      tableData: [],
      searchData: {},
      allColumns: [],
      currentPage: 1,
      currentTotalPage: 1,
      perPage: null,
      loading: false,
      maxHeight: null,
    };
  },
  props: {
    columns: Array,
    // 當有定義selection時，使用此方法來判斷該行資料可否被選擇
    // Function(row, index)
    selectable: Function,
    // 當有定義selection時，是否保留之前選中的資料(必須設定rowKey)
    reserveSelection: Boolean,
    // 當不使用後台排序時，設定對應欄位的排序方法 => {column.name: Function(a, b)}
    sortMethods: {
      type: Object,
      default() {
        return {};
      },
    },
    // 設定欄位的篩選方法 => {column.name: Function(value, row, column)}
    filterMethods: {
      type: Object,
      default() {
        return {};
      },
    },
    formatter: Function,
    headerAlign: {
      type: String,
      default: "center",
    },
    proxy: Function,
    data: Array,
    perPages: {
      type: Array,
      default() {
        return [20, 50, 100];
      },
    },
    showPagination: Boolean,
    showPerPage: {
      type: Boolean,
      default() {
        return true;
      },
    },
    tableHeight: Number,
    zebra: {
      type: Boolean,
      default() {
        return true;
      },
    },
    tableTheme: String
  },
  computed: {
    wrapperClass() {
      return ["tg-table--wrapper"];
    },
    tableClass() {
      return [
        "tg-table",
        {
          zebra: this.zebra,
        },
        this.tableTheme ?? ""
      ];
    },
    tableListener() {
      return {};
    },
    tableProps() {
      let obj = {};
      let keys = Object.keys(ElTableProps);
      keys.forEach((key) => {
        obj[_.kebabCase(key)] = this.$props[key];
      });
      return obj;
    },
    isRemote() {
      return !!this.proxy;
    },
    pagination() {
      return {
        page: this.currentPage,
        perPage: this.perPage,
      };
    },
    loadingLabel() {
      return this.$t("component.table.loading");
    },
    defPerPage() {
      let perPage =
        typeof this.perPages[0] === "object"
          ? this.perPages[0].value
          : this.perPages[0];

      this.perPages.forEach((item) => {
        if (typeof item === "object" && item.active) {
          this.perPage = item.value;
        }
      });

      return perPage;
    },
    isShowPagination() {
      return this.showPagination && this.tableData && this.tableData.length > 0;
    },
  },
  created() {
    let computedColumns = this.parseColumns(this.columns);
    this.computedColumns = computedColumns;
    this.allColumns = this.getAllColumns(computedColumns);
    this.perPage = this.defPerPage;
  },
  mounted() {
    this.parseMaxHeight();
    let tableBody = this.$refs.ElTable.$el.querySelector(
      ".el-table__body-wrapper"
    );

    tableBody.addEventListener("scroll", (e) => {
      this.$emit("on-scroll", e);
    });

    window.addEventListener("resize", this.parseMaxHeight);
  },
  destroyed() {
    window.removeEventListener("resize", this.parseMaxHeight);
  },
  methods: {
    parseColumns(columns) {
      if (!Array.isArray(columns)) {
        return [];
      }

      let computedColumns = [];
      columns.forEach((col) => {
        let column = { ...col };
        column.title = col.langKey
          ? this.$t("column." + col.langKey, { third_party: this.$t('column.'+col.third_party) })
          : col.title;
        
        if ('tip' in col && col.tip.length > 0) {
          column.title += "\n (" + this.$t(col.tip) + ")";
        }

        if (column.children) {
          column.children = this.parseColumns(column.children);
        }
        computedColumns.push(column);
      });
      return computedColumns;
    },
    getAllColumns(columns) {
      const result = [];

      if (!columns || columns.length == 0) {
        return result;
      }

      const indexs = Object.keys(columns);

      indexs.forEach((index) => {
        let column = columns[index];
        if (column.children) {
          result.push(column);
          result.push.apply(result, this.getAllColumns(column.children));
        } else {
          result.push(column);
        }
      });
      return result;
    },
    formatterColumn(row, el_column, cellValue, index) {
      let col = this.allColumns.find((x) => x.name === el_column.columnKey);
      let value = cellValue;

      if (!col) {
        return value;
      }

      if (
        "default" in col &&
        (typeof cellValue == "undefined" ||
          null == cellValue ||
          (typeof cellValue == "string" && !cellValue))
      ) {
        value = col.default;
      }
      
      // column 未定義format
      if (!col.format) {
        return value;
      }

      if (this.formatter) {
        return this.formatter(row, col, value, index);
      }

      // use utils/formatter
      return formatter.format(value, col.format);
    },
    fetch(request) {
      if (this.loading) {
        return;
      }
      if (this.showPagination) {
        request = {
          ...this.pagination,
          ...request,
        };
      }
      this.searchData = request;
      this.loading = true;
      this.proxy(request).then((response) => {
        if(response.retcode == 0){
          this.tableData = response.data;
          this.currentPage = response.page ? response.page : this.currentPage;
          this.perPage = response.per_page ? response.per_page : this.perPage;
          this.currentTotalPage = response.total_page
            ? response.total_page
            : this.currentTotalPage;
          this.$emit("on-loaded", response);
        } else {
          this.tableData = [];
          this.currentPage = 1;
          this.currentTotalPage = 1;
        }
        this.fetchDone();
        this.$nextTick(() => {
          if (this.$refs.ElTable) {
            if (this.$refs.ElTable.$refs.tableHeader) {
              this.$refs.ElTable.$refs.tableHeader.filterPanels = {};
            }
          }
          });
        this.loading = false;
      });
    },
    init(request = {}) {
      this.searchData = {};
      this.fetch({ ...request, page: 1, perPage: this.defPerPage });
    },
    initWithoutFetch() {
      this.searchData = {};
      this.fetchDone();
    },
    doLayout() {
      this.parseMaxHeight();
      this.$nextTick(() => {
        this.$refs.ElTable.doLayout();
      });
    },
    refresh() {
      this.fetch(this.searchData);
    },
    search(request = {}) {
      this.fetch({
        ...request,
        page: 1,
      });
    },
    fetchDone() {
      this.fetched = true;
      this.parseMaxHeight();
      this.$refs.ElTable.$refs.bodyWrapper.scrollTo({ top: 0 });
    },
    handlePageChange(event) {
      this.$emit("on-page-change", event);
      this.fetch({
        // 頁碼變化，取得當前的分頁數量
        ...this.searchData,
        perPage: this.perPage,
        ...event,
      });
    },
    handlePerPageChange(event) {
      this.$emit("on-perPage-change", event);
      this.fetch({
        // 分頁數量變化, 回到第一頁
        ...this.searchData,
        page: 1,
        ...event,
      });
    },
    getParentContent() {
      let parent = this.$el.parentElement;
      let container = parent.parentElement;
      let header = container.querySelector("header");
      let hasScroll = parent.scrollHeight > parent.clientHeight;

      while (parent.tagName != "MAIN") {
        parent = parent.parentNode;
        hasScroll = parent.scrollHeight > parent.clientHeight;
        container = parent.parentElement;
      }

      return {
        container: parent.parentElement,
        parent,
        header,
        hasScroll,
      };
    },
    parseMaxHeight() {
      let parent = this.$layout.$refs.container.$el;
      let hasScroll = parent.scrollHeight > parent.clientHeight;
      // let { parent, hasScroll } = this.getParentContent();

      if (!parent.offsetTop && !parent.clientHeight) {
        this.$nextTick(() => {
          this.doLayout();
        });
      }
      // 確保el-container 宣染後在執行，才可取得正確的高度
      this.$nextTick(() => {
        let headerHeight = 0;
        let showHeaderPaper = this.$store.state.app.showHeaderPaper;

        if (this.$slots["header"]) {
          let header = parent.querySelector("header");
          headerHeight = header.clientHeight;
        }

        let parentHeight = parent.clientHeight - headerHeight - 20;

        if (hasScroll) {
          parentHeight = parent.scrollHeight;
        }

        let maxHeight = parentHeight - (this.isShowPagination ? 80 : 20);

        if (showHeaderPaper) {
          maxHeight = maxHeight - 76; 
        }

        if (this.tableHeight) {
          maxHeight = this.tableHeight - (this.isShowPagination ? 80 : 20);
        }
        this.maxHeight = maxHeight + "px";
      });
    },
    /**
     * 同步滾動軸X
     * @param Number scrollW 參照目標X軸寬度
     * @param Number x 要滾動的數值
     */
    syncScrollX(scrollW, x) {
      let tableBody = this.$refs.ElTable.$el.querySelector(
        ".el-table__body-wrapper"
      );

      let w = tableBody.scrollWidth;

      let sx = (x * w) / scrollW;
      tableBody.scrollTo(sx, 0);
    },
    /**
     * 限制一次只能展開一行
     */
    handleOnExpanded(row, expanded) {
      if (expanded.length <= 1) {
        return;
      }

      expanded.forEach((item) => {
        if (row != item) {
          this.$refs.ElTable.toggleRowExpansion(item);
        }
      });
    },
  },
  watch: {
    columns(val) {
      let computed = this.parseColumns(val);
      this.computedColumns = computed;
      this.allColumns = this.getAllColumns(computed);
    },
    isShowPagination() {
      this.doLayout();
    },
    '$store.state.app.showHeaderPaper': function() {
      this.doLayout();
    }
  },
};
</script>
